import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import Button from 'src/components/Order/Button';
import Header from 'src/components/Order/HeaderComponent';
import MenuSetItemOrder from 'src/components/Order/MenuSetItemOrder';
import PageHeader from 'src/components/Order/PageHeader';
import { PaymentSummaryEntry, paymentSummaryDisplay } from 'earn-ts';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { useEffect, useMemo, useState } from 'react';
import { getCurrentOrderThunk } from 'src/reducer/order/orderThunk';
import Loader from 'src/components/Loader';

export default function OrderBill() {
  const navigate = useNavigate();
  const { language } = useTranslation(['main']).i18n;

  const { t } = useTranslation(['main']);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);

  const handleBack = () => {
    navigate(-1);
  };

  const { costDetail, fullOrder, titleHeader, menuDef, shopId } =
    useAppSelector(state => state.order);

  const [paymentDisplay, setPaymentDisplay] = useState<PaymentSummaryEntry[]>(
    [],
  );

  useEffect(() => {
    if (shopId) {
      dispatch(getCurrentOrderThunk({ shopId: shopId!, withMenu: true }))
        .unwrap()
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, shopId]);

  useEffect(() => {
    if (fullOrder && menuDef) {
      const provider: any = {
        config: () => menuDef,
      };
      const display = paymentSummaryDisplay(
        k => (k.startsWith('item-detail') ? t(k.substring(12)) : t(k)),
        {...fullOrder.table, price: costDetail.totalCost, base_price: costDetail.basePrice },
        fullOrder.payments,
        provider,
        language, {}
      );
      setPaymentDisplay(display.receipt);
    }
  }, [costDetail.basePrice, costDetail.totalCost, fullOrder, menuDef, t, language]);

  const showMenuItems = fullOrder && !!fullOrder.table.json.menu_items.length;

  const dataMenuItems = useMemo(() => {
    const menuItems = fullOrder?.table?.json.menu_items ?? [];
    let timeCreatedWithItems: any = {};
    menuItems.forEach(item => {
      const created = item.created ?? fullOrder?.table.created;
      const items = timeCreatedWithItems[created];
      timeCreatedWithItems[created] = !!items ? [...items, item] : [item];
    });
    return timeCreatedWithItems;
  }, [fullOrder]);

  return (
    <div>
      <div className="top-0 z-20 left-0 fixed px-[15px] pt-[12px] w-full scroll-smooth font-medium shadow-md bg-white">
        <Header
          title={titleHeader}
          isShowBack={false}
          onPressBack={handleBack}
          isShowMenu={true}
        />
      </div>
      <div className="font-medium grow mb-5 w-full overflow-y-auto mt-[52px] bg-white">
        <PageHeader
          title={t('your-order')}
          classNames="bg-[#f7f7f7] pt-[33px]"
        />
        {showMenuItems && (
          <div className="w-full pb-4 border-b-[1.5px] border-solid border-shadow-2">
            {Object.keys(dataMenuItems).map(key => {
              const items = dataMenuItems[key];
              if (items?.[0])
                return (
                  <div
                    key={key}
                    className="border border-solid border-x-0 border-t-0 border-gray-300">
                    <div className="px-[15px] mt-4">
                      <p className="text-[12px]">
                        {moment(Number(key) * 1000).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                      </p>
                    </div>
                    {items?.map((item: any, index: number) => (
                      <div className="w-full pb-4 pt-4" key={index}>
                        <MenuSetItemOrder
                          item={item}
                          lang={language}
                          key={index}
                          classNames="text-[12px]"
                        />
                      </div>
                    ))}
                  </div>
                );
              return <div />;
            })}
          </div>
        )}
        <div className="border-t-[1.5px] border-solid border-shadow-2 bg-white pb-5 fixed bottom-0 left-0 w-full px-[5%]">
          <div className="pt-2 pb-3 flex flex-col">
            {paymentDisplay.map(item => (
              <div
                key={item.label}
                className={twMerge(
                  'flex justify-between text-[12px] text-[#7c7c7c]',
                  item.bold && 'text-black',
                )}>
                <p>{item.label}</p>
                <p>{fullOrder ? item.value : 0}</p>
              </div>
            ))}
          </div>
          <Button
            className="px-5 w-full py-2 text-[16px]"
            onClick={handleBack}
            label={t('back-to-order')}
          />
        </div>
      </div>
      {loading && <Loader loadingText={t('loading')} />}
    </div>
  );
}
